import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from "@chakra-ui/react";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Settings,
  Trash2,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { fetchSwaggerEndpoints } from "../../../helpers/swaggerHelper";
import {
  Library,
  RequestType,
} from "../../../models/workFlow/ExecutionFlowModel";

// Type definitions
interface Claim {
  id: number;
  name: string;
  apiEndpoint: string;
  parameterSource:
  | "claim"
  | "previous_output"
  | "system_variable"
  | "Search_parameter"
  | "selected_Mail_Attachments"
  | "manual"
  | "Lookup";
  parameterKey: string;
  parameterValue: string;
  requestGroup: string;
  isLoadingRequestResponse?: boolean;
  parameterMappings: ParameterMapping[];
  inputJson: string; // Add this
  outputJson: string; // Add this
}
interface ParameterMapping {
  id: number;
  source: string;
  target: string;
  sourceType:
  | "claim"
  | "previous_step"
  | "system_variable"
  | "Search_parameter"
  | "selected_Mail_Attachments"
  | "manual"
  | "Lookup";
  sourceStepId?: number; // Only for previous_step type
  lookupConfig?: string;
}
interface LookupConfiguration {
  endpoint: string;
  inputJson: string;
  outputJson: string;
  mappings: Array<{
    outputPath: string;
    valuePath: string;
  }>;
}

interface UploadAction {
  id: number;
  enabled: boolean;
  apiEndpoint: string;
  parameterMappings: ParameterMapping[];
  inputJson: string;
  outputJson: string;
}

interface FlowStep {
  id: number;
  apiEndpoint: string;
  parameters: Record<string, any>;
  parameterMappings: ParameterMapping[];
  condition: string;
  order: number;
  inputJson: string;
  outputJson: string;
  requestGroup: string;
  isLoadingRequestResponse?: boolean;
  uploadAction?: UploadAction; // Add this
  outputMappings?: OutputMapping[];
}

interface StepCardProps {
  step: FlowStep;
  stepNumber: number;
  swaggerEndpoints: RequestType[];
  onUpdateStep: (stepId: number, field: keyof FlowStep, value: any) => void;
  onUpdateParameter: (
    stepId: number,
    paramName: string,
    paramValue: string
  ) => void;
  onUpdateParameterMapping: (stepId: number, mapping: ParameterMapping) => void;
  onRemoveParameterMapping: (stepId: number, mappingId: number) => void;
  onAddParameterMapping: (stepId: number) => void;
  onRemoveStep: (stepId: number) => void;
  onRequestTypeSelect: (
    stepId: number,
    item: RequestType,
    requestGroup: string
  ) => Promise<void>;
  claims: Claim[];
  previousSteps: FlowStep[];
  accessToken: string;
  conId: number;
}

interface ClaimCardProps {
  claim: Claim;
  claimNumber: number;
  swaggerEndpoints: RequestType[];
  isSwaggerEnabled: boolean; // New prop to indicate if Swagger is enabled
  swaggerUrl?: string; // New prop for Swagger URL

  onUpdateClaim: (claimId: number, field: keyof Claim, value: any) => void;
  onRemoveClaim: (claimId: number) => void;
  onRequestTypeSelect: (
    claimId: number,
    item: RequestType,
    requestGroup: string,
    isSwaggerEnabled: boolean,
    swaggerUrl: string

  ) => Promise<void>;
}

interface GroupedEndpoints {
  [key: string]: RequestType[];
}

interface OutputMapping {
  outputPath: string;
  libraryField: keyof Library;
}

const ClaimCard: React.FC<ClaimCardProps> = ({
  claim,
  claimNumber,
  swaggerEndpoints,
  onUpdateClaim,
  onRemoveClaim,
  onRequestTypeSelect,
  isSwaggerEnabled, // Determines if Swagger is enabled
  swaggerUrl,
}) => {
  const [endpoints, setEndpoints] = useState<RequestType[]>(swaggerEndpoints);
  const [showAdvanced, setShowAdvanced] = useState(true);
  const [manualRequest, setManualRequest] = useState(claim.inputJson || ""); // For manual request entry
  const [manualResponse, setManualResponse] = useState(claim.outputJson || ""); // For manual response entry
  const [manualRequestGroup, setManualRequestGroup] = useState(
    claim.requestGroup || ""
  ); // For manual request group

  // Group endpoints by type
  const groupedEndpoints: GroupedEndpoints = swaggerEndpoints.reduce(
    (acc, endpoint) => {
      const group = endpoint.type || "Uncategorized";
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(endpoint);
      return acc;
    },
    {} as GroupedEndpoints
  );

  const selectedEndpoint = swaggerEndpoints.find(
    (ep) => ep.name === claim.apiEndpoint
  );

  useEffect(() => {
    if (isSwaggerEnabled && swaggerUrl) {
      fetchSwaggerEndpoints(swaggerUrl).then((data) => {
        setEndpoints(data);
      });
    }
  }, [isSwaggerEnabled, swaggerUrl]);

  const handleEndpointChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    const selectedEndpointName = e.target.value;
    const selectedEndpoint = swaggerEndpoints.find(
      (ep) => ep.name === selectedEndpointName
    );
    if (selectedEndpoint) {
      onRequestTypeSelect(
        claim.id,
        selectedEndpoint,
        selectedEndpoint.type || "",
        isSwaggerEnabled,
        swaggerUrl || ""
      );
    }
  };

  const handleManualInputChange = (
    field: "inputJson" | "outputJson" | "apiEndpoint" | "requestGroup",
    value: string
  ) => {
    if (field === "inputJson") {
      setManualRequest(value);
      onUpdateClaim(claim.id, "inputJson", value);
    } else if (field === "outputJson") {
      setManualResponse(value);
      onUpdateClaim(claim.id, "outputJson", value);
    } else if (field === "apiEndpoint") {
      onUpdateClaim(claim.id, "apiEndpoint", value);
    } else if (field === "requestGroup") {
      setManualRequestGroup(value);
      onUpdateClaim(claim.id, "requestGroup", value); // Store the selected request group
    }
  };

  const handleMappingChange = (
    mappingId: number,
    field: keyof ParameterMapping,
    value: string
  ) => {
    const updatedMappings = claim.parameterMappings.map((mapping) =>
      mapping.id === mappingId ? { ...mapping, [field]: value } : mapping
    );
    onUpdateClaim(claim.id, "parameterMappings", updatedMappings);
  };

  const addParameterMapping = () => {
    const newMapping: ParameterMapping = {
      id: Date.now(),
      source: "",
      target: "",
      sourceType: "system_variable",
    };
    onUpdateClaim(claim.id, "parameterMappings", [
      ...claim.parameterMappings,
      newMapping,
    ]);
  };

  const removeParameterMapping = (mappingId: number) => {
    const updatedMappings = claim.parameterMappings.filter(
      (mapping) => mapping.id !== mappingId
    );
    onUpdateClaim(claim.id, "parameterMappings", updatedMappings);
  };

  const getAvailableSources = (): Array<{
    value: string;
    label: string;
    type: "system_variable";
    details?: string;
  }> => {
    return [
      {
        value: "user.id",
        label: "System Variable",
        type: "system_variable",
        details: "User ID",
      },
      {
        value: "user.mail",
        label: "System Variable",
        type: "system_variable",
        details: "User Email",
      },
      {
        value: "user.tenantId",
        label: "System Variable",
        type: "system_variable",
        details: "Tenant ID",
      },
    ];
  };

  const getAvailableTargets = (): Array<{
    value: string;
    label: string;
    details?: string;
  }> => {
    const targets: Array<{ value: string; label: string; details?: string }> =
      [];

    try {
      const inputJson = claim.inputJson ? JSON.parse(claim.inputJson) : {};
      Object.keys(inputJson).forEach((key) => {
        targets.push({
          value: key,
          label: "Input Parameter",
          details: key,
        });
      });
    } catch (e) {
      console.error("Error parsing claim input value", e);
    }

    return targets;
  };

  return (
    <Card>
      <CardBody>
        <VStack spacing={4} align="stretch">
          {/* Claim Header */}
          <Flex justify="space-between" align="center">
            <HStack spacing={3}>
              <Badge
                colorScheme="purple"
                fontSize="sm"
                px={3}
                py={1}
                borderRadius="full"
              >
                Claim {claimNumber}
              </Badge>
              <Input
                placeholder="Claim name (e.g., User ID)"
                value={claim.name}
                onChange={(e) =>
                  onUpdateClaim(claim.id, "name", e.target.value)
                }
                size="sm"
                width="auto"
                bg="white"
                flex={1}
              />
              {claim.isLoadingRequestResponse && (
                <Spinner size="sm" color="blue.500" />
              )}
            </HStack>
            <HStack>
              <Tooltip label={showAdvanced ? "Hide Advanced" : "Show Advanced"}>
                <IconButton
                  icon={
                    showAdvanced ? (
                      <ChevronDown size={16} />
                    ) : (
                      <ChevronRight size={16} />
                    )
                  }
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  aria-label={showAdvanced ? "Hide Advanced" : "Show Advanced"}
                />
              </Tooltip>
              <Tooltip label="Remove Claim">
                <IconButton
                  icon={<Trash2 size={16} />}
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => onRemoveClaim(claim.id)}
                  aria-label="Remove Claim"
                />
              </Tooltip>
            </HStack>
          </Flex>

          {/* Configuration */}
          <VStack spacing={4} align="stretch">
            {isSwaggerEnabled ? (
              <>
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.600">
                    <HStack spacing={2}>
                      <Settings size={14} />
                      <Text>API Endpoint to Fetch Claim</Text>
                    </HStack>
                  </FormLabel>
                  <Select
                    placeholder="Select API endpoint..."
                    value={claim.apiEndpoint}
                    onChange={handleEndpointChange}
                    bg="white"
                    size="sm"
                    disabled={claim.isLoadingRequestResponse}
                  >
                    {Object.entries(groupedEndpoints).map(
                      ([type, endpoints]) => (
                        <optgroup key={type} label={type}>
                          {endpoints.map((endpoint) => (
                            <option key={endpoint.name} value={endpoint.name}>
                              {endpoint.name}
                            </option>
                          ))}
                        </optgroup>
                      )
                    )}
                  </Select>
                </FormControl>

                {/* API Details */}
                {selectedEndpoint && (
                  <Box bg="gray.50" p={3} borderRadius="md">
                    <VStack align="stretch" spacing={3}>
                      <HStack spacing={4}>
                        <Badge colorScheme="green" size="sm">
                          {selectedEndpoint.type}
                        </Badge>
                        <Text fontSize="sm" fontFamily="mono" color="gray.600">
                          {selectedEndpoint.name}
                        </Text>
                        {claim.requestGroup && (
                          <Badge colorScheme="purple" size="sm">
                            {claim.requestGroup}
                          </Badge>
                        )}
                      </HStack>

                      {/* Input JSON Display */}
                      {claim.inputJson && (
                        <Box>
                          <Text
                            fontSize="sm"
                            fontWeight="600"
                            color="gray.700"
                            mb={2}
                          >
                            Input JSON:
                          </Text>
                          <Box
                            as="pre"
                            bg="white"
                            p={2}
                            borderRadius="md"
                            fontSize="xs"
                            overflow="auto"
                            maxH="150px"
                            border="1px solid"
                            borderColor="gray.200"
                            whiteSpace="pre-wrap"
                          >
                            {typeof claim.inputJson === "string"
                              ? claim.inputJson
                              : JSON.stringify(claim.inputJson, null, 2)}
                          </Box>
                        </Box>
                      )}

                      {/* Output JSON Display */}
                      {claim.parameterValue && (
                        <Box>
                          <Text
                            fontSize="sm"
                            fontWeight="600"
                            color="gray.700"
                            mb={2}
                          >
                            Output JSON:
                          </Text>
                          <Box
                            as="pre"
                            bg="white"
                            p={2}
                            borderRadius="md"
                            fontSize="xs"
                            overflow="auto"
                            maxH="150px"
                            border="1px solid"
                            borderColor="gray.200"
                            whiteSpace="pre-wrap"
                          >
                            {typeof claim.outputJson === "string"
                              ? claim.outputJson
                              : JSON.stringify(claim.outputJson, null, 2)}
                          </Box>
                        </Box>
                      )}
                    </VStack>
                  </Box>
                )}
              </>
            ) : (
              // Manual configuration
              <>
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.600">
                    API Endpoint
                  </FormLabel>
                  <Input
                    placeholder="Enter API endpoint..."
                    value={claim.apiEndpoint}
                    onChange={(e) =>
                      handleManualInputChange("apiEndpoint", e.target.value)
                    }
                    size="sm"
                    bg="white"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.600">
                    Request Method
                  </FormLabel>
                  <Select
                    placeholder="Select request method..."
                    value={manualRequestGroup}
                    onChange={(e) =>
                      handleManualInputChange("requestGroup", e.target.value)
                    }
                    bg="white"
                    size="sm"
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                    <option value="DELETE">DELETE</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.600">
                    Request JSON
                  </FormLabel>
                  <Input
                    placeholder="Enter request JSON..."
                    value={manualRequest}
                    onChange={(e) =>
                      handleManualInputChange("inputJson", e.target.value)
                    }
                    size="sm"
                    bg="white"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.600">
                    Response JSON
                  </FormLabel>
                  <Input
                    placeholder="Enter response JSON..."
                    value={manualResponse}
                    onChange={(e) =>
                      handleManualInputChange("outputJson", e.target.value)
                    }
                    size="sm"
                    bg="white"
                  />
                </FormControl>
              </>
            )}
            {/* Advanced Configuration - Parameter Mappings */}
            {showAdvanced && (
              <Box>
                <Text fontSize="sm" fontWeight="600" color="gray.700" mb={2}>
                  Parameter Mappings:
                </Text>

                {claim.parameterMappings.length > 0 ? (
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>System Variable</Th>
                        <Th>Input Parameter</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {claim.parameterMappings.map((mapping) => (
                        <Tr key={mapping.id}>
                          <Td>
                            <Select
                              value={mapping.source}
                              onChange={(e) =>
                                handleMappingChange(
                                  mapping.id,
                                  "source",
                                  e.target.value
                                )
                              }
                              size="sm"
                              bg="white"
                            >
                              <option value="">Select variable...</option>
                              {getAvailableSources().map((source) => (
                                <option key={source.value} value={source.value}>
                                  {source.details}
                                </option>
                              ))}
                            </Select>
                          </Td>
                          <Td>
                            <Select
                              value={mapping.target}
                              onChange={(e) =>
                                handleMappingChange(
                                  mapping.id,
                                  "target",
                                  e.target.value
                                )
                              }
                              size="sm"
                              bg="white"
                            >
                              <option value="">Select parameter...</option>
                              {getAvailableTargets().map((target) => (
                                <option key={target.value} value={target.value}>
                                  {target.details}
                                </option>
                              ))}
                            </Select>
                          </Td>
                          <Td>
                            <IconButton
                              icon={<Trash2 size={14} />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => removeParameterMapping(mapping.id)}
                              aria-label="Remove mapping"
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                ) : (
                  <Text
                    fontSize="sm"
                    color="gray.500"
                    textAlign="center"
                    py={4}
                  >
                    No parameter mappings defined yet
                  </Text>
                )}

                <Button
                  leftIcon={<Plus size={14} />}
                  size="sm"
                  variant="outline"
                  mt={2}
                  onClick={addParameterMapping}
                >
                  Add Mapping
                </Button>
              </Box>
            )}
          </VStack>
        </VStack>
      </CardBody>
    </Card>
  );
};

export default ClaimCard;
